import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Scene } from '../../types';
import { AssetField } from './AssetPickerModal';
import { assetUrl } from './scenario-api';
import {
  InspectObjectConfig,
  ObjectInspectSceneConfig,
  applyObjectInspectScene,
  defaultObjectInspectConfig,
  parseObjectInspectScene,
} from './object-inspect-editor-model';
import { uniqueId } from './utils';
import { PedaCategorySelect } from './PedaCategorySelect';
import styles from './scenario-studio.module.css';

interface ObjectInspectEditorProps {
  scenarioId: string;
  scene: Scene;
  sceneIds: string[];
  selectedElementId?: string | null;
  onSelectedElementIdChange?: (id: string | null) => void;
  onChange: (scene: Scene) => void;
}

function defaultObject(sceneId: string): InspectObjectConfig {
  const id = uniqueId('obj');
  return {
    id,
    position: { x: 50, y: 50, width: 10, height: 10 },
    inspectable: true,
    role: 'anomaly',
    reportMessage: '',
    dismissMessage: '',
    pedaShields: 1,
    pedaCategory: 'appareils',
  };
}

export const ObjectInspectEditor: React.FC<ObjectInspectEditorProps> = ({
  scenarioId,
  scene,
  sceneIds,
  selectedElementId = null,
  onSelectedElementIdChange,
  onChange,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  const selectObject = useCallback(
    (id: string | null) => {
      setSelectedId(id);
      onSelectedElementIdChange?.(id);
    },
    [onSelectedElementIdChange]
  );

  useEffect(() => {
    if (selectedElementId) {
      setSelectedId(selectedElementId);
    }
  }, [selectedElementId, scene.id]);

  useEffect(() => {
    if (!selectedId) return;
    const sidebarItem = document.getElementById(`element-editor-item-${selectedId}`);
    sidebarItem?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    const previewItem = document.getElementById(`element-editor-preview-${selectedId}`);
    previewItem?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedId, scene.id]);

  const config = useMemo(() => parseObjectInspectScene(scene), [scene]);
  const selected = config.objects.find((object) => object.id === selectedId) ?? null;

  const commit = useCallback(
    (nextConfig: ObjectInspectSceneConfig) => {
      onChange(applyObjectInspectScene(scene, nextConfig));
    },
    [onChange, scene]
  );

  const patchConfig = (patch: Partial<ObjectInspectSceneConfig>) => {
    commit({ ...config, ...patch });
  };

  const patchObject = (id: string, patch: Partial<InspectObjectConfig>) => {
    commit({
      ...config,
      objects: config.objects.map((object) => (object.id === id ? { ...object, ...patch } : object)),
    });
  };

  const addObject = () => {
    const object = defaultObject(scene.id);
    commit({ ...config, objects: [...config.objects, object] });
    selectObject(object.id);
  };

  const removeObject = (id: string) => {
    commit({ ...config, objects: config.objects.filter((object) => object.id !== id) });
    if (selectedId === id) selectObject(null);
  };

  const updatePositionFromPointer = useCallback(
    (objectId: string, clientX: number, clientY: number) => {
      const preview = previewRef.current;
      if (!preview) return;
      const rect = preview.getBoundingClientRect();
      const x = Math.min(100, Math.max(0, Math.round(((clientX - rect.left) / rect.width) * 1000) / 10));
      const y = Math.min(100, Math.max(0, Math.round(((clientY - rect.top) / rect.height) * 1000) / 10));
      const current = parseObjectInspectScene(scene);
      const target = current.objects.find((object) => object.id === objectId);
      if (!target) return;
      commit({
        ...current,
        objects: current.objects.map((object) =>
          object.id === objectId ? { ...object, position: { ...object.position, x, y } } : object
        ),
      });
    },
    [commit, scene]
  );

  useEffect(() => {
    if (!draggingId) return;
    const onMove = (e: MouseEvent) => updatePositionFromPointer(draggingId, e.clientX, e.clientY);
    const onUp = () => setDraggingId(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [draggingId, updatePositionFromPointer]);

  const anomalyCount = config.objects.filter((object) => object.role === 'anomaly' && object.inspectable).length;

  return (
    <div className={styles.objectInspectEditor}>
      <div className={styles.panelHeader}>
        <h3>Scène d&apos;observation</h3>
        <span className={styles.inspectSummary}>
          {config.objects.length} objet{config.objects.length !== 1 ? 's' : ''} · {anomalyCount} anomalie{anomalyCount !== 1 ? 's' : ''}
        </span>
      </div>

      <div className={styles.inspectSceneSettings}>
        <div className={styles.field}>
          <label>Consigne (affichée tant que le joueur n&apos;a pas tout signalé)</label>
          <textarea
            className={styles.textarea}
            rows={2}
            value={config.instruction}
            onChange={(e) => patchConfig({ instruction: e.target.value })}
          />
        </div>
        <div className={styles.field}>
          <label>Texte au-dessus de Continuer (quand toutes les anomalies sont signalées)</label>
          <textarea
            className={styles.textarea}
            rows={2}
            value={config.exitPrompt}
            onChange={(e) => patchConfig({ exitPrompt: e.target.value })}
            placeholder="Ex. : Les signaux faibles sont repérés. Vous pouvez poursuivre."
          />
        </div>
        <div className={styles.field}>
          <label>Texte du bouton de sortie</label>
          <input
            className={styles.input}
            value={config.choicePrompt}
            onChange={(e) => patchConfig({ choicePrompt: e.target.value })}
          />
        </div>
        <div className={styles.inspectSettingsRow}>
          <div className={styles.field}>
            <label>Scène suivante (optionnel — vide = scène suivante dans le flux)</label>
            <select
              className={styles.select}
              value={config.continueGotoScene}
              onChange={(e) => patchConfig({ continueGotoScene: e.target.value })}
            >
              <option value="">— Choisir —</option>
              {sceneIds.filter((id) => id !== scene.id).map((id) => (
                <option key={id} value={id}>{id}</option>
              ))}
            </select>
          </div>
        </div>
        <div className={styles.field}>
          <label>Dialogues quand toutes les anomalies sont signalées</label>
          {config.completionDialogues.map((line, index) => (
            <div key={index} className={styles.completionDialogueRow}>
              <textarea
                className={styles.textarea}
                rows={2}
                value={line}
                onChange={(e) => {
                  const completionDialogues = [...config.completionDialogues];
                  completionDialogues[index] = e.target.value;
                  patchConfig({ completionDialogues });
                }}
              />
              <button
                type="button"
                className={styles.btnDanger}
                onClick={() =>
                  patchConfig({
                    completionDialogues: config.completionDialogues.filter((_, i) => i !== index),
                  })
                }
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={() => patchConfig({ completionDialogues: [...config.completionDialogues, ''] })}
          >
            + Dialogue de complétion
          </button>
        </div>
      </div>

      <div className={styles.elementsLayout}>
        <div
          ref={previewRef}
          className={styles.scenePreview}
          style={{
            backgroundImage: scene.background
              ? `url(${assetUrl(scenarioId, scene.background)})`
              : undefined,
          }}
        >
          {config.objects.map((object) => {
            const isAnomaly = object.inspectable && object.role === 'anomaly';
            return (
              <div
                key={object.id}
                id={`element-editor-preview-${object.id}`}
                className={`${styles.previewElement} ${styles.inspectPreviewElement} ${selectedId === object.id ? styles.selected : ''} ${isAnomaly ? styles.inspectAnomaly : styles.inspectDecoy}`}
                style={{
                  left: `${object.position.x}%`,
                  top: `${object.position.y}%`,
                  width: object.position.width ? `${object.position.width}%` : 'auto',
                  height: object.position.height ? `${object.position.height}%` : 'auto',
                  transform: 'translate(-50%, -50%)',
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectObject(object.id);
                  setDraggingId(object.id);
                }}
              >
                {object.image && <img src={assetUrl(scenarioId, object.image)} alt="" />}
                <span className={styles.elementLabel}>
                  {isAnomaly ? '⚠ ' : ''}{object.id}
                </span>
              </div>
            );
          })}
        </div>

        <div className={styles.elementsSidebar}>
          <div className={styles.row}>
            <button type="button" className={styles.btnSecondary} onClick={addObject}>
              + Objet
            </button>
          </div>

          {config.objects.map((object) => (
            <button
              key={object.id}
              id={`element-editor-item-${object.id}`}
              type="button"
              className={`${styles.listItem} ${selectedId === object.id ? styles.active : ''}`}
              onClick={() => selectObject(object.id)}
            >
              {object.role === 'anomaly' ? '⚠ ' : '○ '}
              {object.id}
              {!object.inspectable ? ' (décor)' : ''}
            </button>
          ))}

          {selected && (
            <div className={styles.card}>
              <div className={styles.field}>
                <label>ID</label>
                <input
                  className={styles.input}
                  value={selected.id}
                  onChange={(e) => {
                    const newId = e.target.value;
                    commit({
                      ...config,
                      objects: config.objects.map((object) =>
                        object.id === selected.id ? { ...object, id: newId } : object
                      ),
                    });
                    setSelectedId(newId);
                    onSelectedElementIdChange?.(newId);
                  }}
                />
              </div>

              <div className={styles.field}>
                <label>
                  <input
                    type="checkbox"
                    checked={selected.inspectable}
                    onChange={(e) => patchObject(selected.id, { inspectable: e.target.checked })}
                  />
                  {' '}Cliquable à l&apos;écran
                </label>
              </div>

              {selected.inspectable && (
                <>
                  <div className={styles.field}>
                    <label>Type d&apos;objet</label>
                    <select
                      className={styles.select}
                      value={selected.role}
                      onChange={(e) => {
                        const role = e.target.value as InspectObjectConfig['role'];
                        patchObject(selected.id, {
                          role,
                          pedaShields: role === 'anomaly' ? Math.max(selected.pedaShields, 1) : 0,
                        });
                      }}
                    >
                      <option value="anomaly">Anomalie (à signaler)</option>
                      <option value="decoy">Élément neutre (leurre)</option>
                    </select>
                  </div>

                  {selected.role === 'anomaly' && (
                    <>
                    <div className={styles.field}>
                      <label>Boucliers PEDA gagnés</label>
                      <input
                        type="number"
                        min={1}
                        className={styles.input}
                        value={selected.pedaShields}
                        onChange={(e) =>
                          patchObject(selected.id, { pedaShields: parseInt(e.target.value, 10) || 1 })
                        }
                      />
                    </div>
                    <PedaCategorySelect
                      value={selected.pedaCategory ?? ''}
                      onChange={(pedaCategory) => patchObject(selected.id, { pedaCategory })}
                      required
                    />
                    </>
                  )}

                  <div className={styles.field}>
                    <label>Message si le joueur clique « Signaler »</label>
                    <textarea
                      className={styles.textarea}
                      rows={3}
                      value={selected.reportMessage}
                      onChange={(e) => patchObject(selected.id, { reportMessage: e.target.value })}
                      placeholder={
                        selected.role === 'anomaly'
                          ? 'Réaction du personnage après avoir signalé une vraie anomalie…'
                          : 'Réaction si le joueur signale à tort un élément neutre…'
                      }
                    />
                  </div>

                  <div className={styles.field}>
                    <label>Message si le joueur clique « Rien à signaler »</label>
                    <textarea
                      className={styles.textarea}
                      rows={3}
                      value={selected.dismissMessage}
                      onChange={(e) => patchObject(selected.id, { dismissMessage: e.target.value })}
                      placeholder="Réaction si le joueur ignore cet objet…"
                    />
                  </div>
                </>
              )}

              <AssetField
                scenarioId={scenarioId}
                label="Image"
                value={selected.image ?? ''}
                subdir="elements"
                onChange={(image) => patchObject(selected.id, { image })}
              />

              {['x', 'y', 'width', 'height'].map((key) => (
                <div key={key} className={styles.field}>
                  <label>{key} (%)</label>
                  <input
                    className={styles.input}
                    type="number"
                    value={(selected.position as Record<string, number>)[key] ?? 0}
                    onChange={(e) =>
                      patchObject(selected.id, {
                        position: {
                          ...selected.position,
                          [key]: parseFloat(e.target.value) || 0,
                        },
                      })
                    }
                  />
                </div>
              ))}

              <button type="button" className={styles.btnDanger} onClick={() => removeObject(selected.id)}>
                Supprimer l&apos;objet
              </button>
            </div>
          )}

          {config.objects.length === 0 && (
            <p className={styles.hint}>
              Ajoutez des objets cliquables. Les anomalies (⚠) donnent des boucliers PEDA quand le joueur clique « Signaler ».
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export function initializeObservationScene(scene: Scene): Scene {
  const config = parseObjectInspectScene(scene);
  if (config.objects.length > 0) {
    return applyObjectInspectScene(scene, config);
  }
  const base = defaultObjectInspectConfig(scene.id);
  return applyObjectInspectScene(scene, {
    ...base,
    objects: config.objects,
  });
}
